import { useEffect, useState } from "react";
import { Box, Button, Dialog } from "@mui/material";
import GridCellChipsContent from "../../components/GridCellChipsContent";
import Loader from "../../components/Loader";
import { Permissions, PermissionsFriendlyNames, SortDirection } from "../../models/Enum";
import SessionService from "../../services/SessionService";
import Filters from "./Filters";
import { DataGrid, GridActionsCellItem, GridColDef, GridPaginationModel, GridRowModesModel, GridRowParams, GridSortModel, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarDensitySelector, GridToolbarExport, GridToolbarQuickFilter, GridValidRowModel } from "@mui/x-data-grid";
import { useAppDispatch, useAppSelector } from "../../store";
import api from "../../services/ApiService";
import { ApiResponse } from "../../models/ApiResponse";
import { initialProductAssignmentsListRequest, updateProductAssignmentsListRequest } from "../../reducers/productAssignmentReducer";
import { SelectedFilters } from "../../models/Items/ItemListRequest";
import { KeyValues } from "../../models/Items/FilterOptions";
import EditIcon from "@mui/icons-material/Edit";
import { NO_LOCAL_SEARCH_RESULTS_GRID_MESSAGE, NO_ROWS_GRID_MESSAGE } from "../../components/GridUtilityComponents";
import { toast } from "react-toastify";
import { VIEW_PRODUCT_ASSIGNMENT } from "./EditItem/Constants";
import UserAssignments, { UserAssignmentFieldValue, UserDropdownOptions } from "../../components/UserAssignments";
import { UserAssignment } from "../../models/UserAssignment";
import ConfirmationModal from "../../components/ConfirmationModal";
import CloseIcon from '@mui/icons-material/Close';
import { Item } from "../../models/Items/Item";
import ExportProductAssignmentListing from "../../components/ExportProductAssignmentListing";
import { sortByKey } from "../../utils/helpers";

export type ProductAssignments = Partial<Item> & {
    stockCodeCount: number;
};

type UserAssignmentWithName = UserAssignment & {
    name: string;
};

export interface ProductAssignmentsResponse {
    productAssignments?: ProductAssignments[];
    totalCount?: number;
}

const AssignmentList = () => {

    const initialUserAssignment: UserAssignment = {
        userId: "",
        assignmentId: "",
        id: 0
    };

    const [isLoading, setLoading] = useState(false);
    const [fetchedUserAssignments, setFetchedUserAssignments] = useState<ProductAssignments[]>([]);
    const [showAutoAssignmentConfirmModal, setShowAutoAssignmentConfirmModal] = useState(false);
    const [userAssignmentsTotalCount, setUserAssignmentsTotalCount] = useState(0);
    const [filtersExpanded, setFiltersExpanded] = useState<boolean>(true);
    const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
    const [selectedUserAssignments, setSelectedUserAssignments] = useState<UserAssignmentFieldValue[]>([initialUserAssignment]);
    const [assignmentRoleDropdownOptions, setAssignmentRoleDropdownOptions] = useState<KeyValues[]>([]);
    const [userDropdownOptions, setUserDropdownOptions] = useState<UserDropdownOptions[]>([]);
    const [assignmentModal, setAssignmentModal] = useState<boolean>(false);
    const [selectedAssignmentRow, setSelectedAssignmentRow] = useState<ProductAssignments>();
    const [selectedDeleteRecord, setSelectedDeleteRecord] = useState<UserAssignmentWithName>();

    const dispatch = useAppDispatch();
    const productAssignmentsListRequestState = useAppSelector((state) => state.productAssignments.productAssignmentsListRequest);
    const sessionService = SessionService.getInstance();


    const ProductAssignmentListReferenceColumnsMapping = {
        baseCode: "baseCode",
        marketingDesignerDisplayText: "marketingDesigner",
        seriesNameDisplayText: "seriesName",
        shortDesc: "shortDesc"
    };

    const getAssignments = async () => {
        api
        .get<ApiResponse<any[]>>(`/Assignment/list`)
        .then((response: ApiResponse<any[]>) => {
            if (response.isSuccess) {
                const assignments = response.data?.length > 0
                    ? response.data.filter(a => a.isActive === true)
                        .map(u => {
                            return {
                                id: u.id,
                                name: u.name
                            };
                        })
                    : [];
                setAssignmentRoleDropdownOptions((Array.isArray(assignments) ? assignments.sort(sortByKey()) : []) as KeyValues[]);
            } else {
                throw new Error(response?.message);
            }
        })
        .catch((error) => {
            console.error("Exception from Assignments", error);
        });
    }

    const getUsersAvailableForAssignment = async (baseCode?: string) => {
        if (!baseCode) {
            setUserDropdownOptions([]);
            return;
        }

        try {
            const response = await api.get<ApiResponse<UserDropdownOptions[]>>(`/Assignment/product-assignment/${baseCode}/users`);
            if (response.isSuccess) {
                const users = (response.data || []).map(ul => ({
                    id: ul.id,
                    name: ul.name
                }));
                setUserDropdownOptions((Array.isArray(users) ? users.sort(sortByKey()) : []) as UserDropdownOptions[]);
            } else {
                throw new Error(response?.message);
            }
        } catch (error) {
            setUserDropdownOptions([]);
            console.error("Exception from users available for assignment", error);
        }
    }

    const handleOpenAssignmentModal = async (row: ProductAssignments) => {
        setSelectedAssignmentRow(row);
        setSelectedUserAssignments(row.userAssignments?.length > 0
            ? row.userAssignments.map(usrAssignment => ({ id: usrAssignment.id, userId: usrAssignment.userId, assignmentId: usrAssignment.assignmentId }))
            : [{ userId: "", assignmentId: "" }]);
        setLoading(true);
        await getUsersAvailableForAssignment(row.baseCode);
        setLoading(false);
        setAssignmentModal(true);
    }

    const actionColumn = {
        field: "actions",
        type: "actions",
        headerName: "Action",
        disableColumnMenu: true,
        getActions: (params: GridRowParams) => ([
            <GridActionsCellItem
                key={params.row.id}
                icon={<EditIcon />}
                onClick={() => handleOpenAssignmentModal(params.row)}
                label="Edit"
            />,
        ])
    };

    useEffect(() => {
        getProductAssignments();
    }, [productAssignmentsListRequestState]);

    const handleExpandClick = () => {
        setFiltersExpanded(!filtersExpanded);
    };

    useEffect(() => {
        getAssignments();
    }, [])

    const getProductAssignments = async () => {
        try {
            setLoading(true);
            api
                .post<ApiResponse<ProductAssignmentsResponse>>("/Assignment/product-assignment/list", productAssignmentsListRequestState)
                .then(async (response) => {
                    if (response.isSuccess) {
                        setUserAssignmentsTotalCount(response?.data?.totalCount);
                        let productAssignments: ProductAssignments[] = (response?.data?.productAssignments || []);
                        if (productAssignments?.length > 0) {
                            productAssignments = productAssignments.map((pa, i) => {
                                const uniqueAssignments = [];
                                if (pa.userAssignments?.length > 0) {
                                    pa.userAssignments.map(ua => {
                                        // remove duplicate id records
                                        if (!uniqueAssignments.find(item => item.id === ua.id)?.id) {
                                            uniqueAssignments.push(ua);
                                        }
                                    });
                                }
                                return {
                                    ...pa,
                                    userAssignments: uniqueAssignments
                                };
                            });
                        };

                        setFetchedUserAssignments(productAssignments);
                        setSelectedUserAssignments(undefined);
                        setSelectedDeleteRecord(undefined);
                        setSelectedAssignmentRow(undefined);
                    } else {
                        throw new Error(response.message);
                    }
                    setLoading(false);
                })
                .catch((error) => {
                    setLoading(false);
                    console.error("Exception from product assignments list", error);
                });
        } catch (error: any) {
            setLoading(false);
            console.error("Exception from product assignments list", error);
        }
    };

    const updateProductAssignments = async (type?: "update" | "delete", userAssignments?: UserAssignmentFieldValue[]) => {
        setLoading(true);
        api
            .post<ApiResponse<any>>(`/Assignment/product-assignment/${selectedAssignmentRow?.baseCode || ""}`, userAssignments || selectedUserAssignments)
            .then(async (response) => {
                if (response.isSuccess) {
                    toast.success(type === "delete" ? "Product assignment deleted successfully" : response.message);
                    await getProductAssignments();
                }
            })
            .catch((error) => {
                toast.error(`Error while ${type === "delete" ? "deleting" : "updating"} product assignments.`);
                console.error("Exception from update product assignment", error);
            });
        setLoading(false);
    };

    // if sorted by a field which is not direct field of product table then find the correct column from mapping
    const getSortFieldName = (gridFieldName: string): string => {
        return ProductAssignmentListReferenceColumnsMapping[gridFieldName] || gridFieldName;
    };

    // convert state sortBy value to column name that is bound in grid
    const getReverseSortFieldName = (fieldName: string): string => {
        const gridFieldName = Object.keys(ProductAssignmentListReferenceColumnsMapping).find(
            (prmKey) => ProductAssignmentListReferenceColumnsMapping[prmKey] === fieldName
        );
        return gridFieldName || fieldName;
    };

    const onSortChange = (sorting: GridSortModel) => {
        if (sorting?.length === 0) {
            dispatch(updateProductAssignmentsListRequest({
                ...productAssignmentsListRequestState,
                sortBy: productAssignmentsListRequestState.sortBy || initialProductAssignmentsListRequest.sortBy,
                sortDirection: productAssignmentsListRequestState?.sortDirection === SortDirection.DESC ? SortDirection.ASC : SortDirection.DESC
            }));
        } else {
            dispatch(updateProductAssignmentsListRequest({
                ...productAssignmentsListRequestState,
                sortBy: getSortFieldName(sorting[0].field),
                sortDirection:
                    sorting[0].sort === "asc" ? SortDirection.ASC : SortDirection.DESC,
            }));
        }
    };

    const onPaginationChange = (pagination: GridPaginationModel) => {
        dispatch(updateProductAssignmentsListRequest({
            ...productAssignmentsListRequestState,
            pageIndex: pagination.page + 1,
            pageSize: pagination.pageSize,
        }));
    };

    const handleApplyFilterClick = (filterValues: SelectedFilters) => {
        if (Object.values(filterValues)?.length) {
            dispatch(updateProductAssignmentsListRequest({
                pageSize: productAssignmentsListRequestState.pageSize,
                sortBy: productAssignmentsListRequestState.sortBy,
                sortDirection: productAssignmentsListRequestState.sortDirection,
                ...filterValues,
                pageIndex: 1,
            }));
        } else {
            //reset the filters
            dispatch(updateProductAssignmentsListRequest(initialProductAssignmentsListRequest));
        }
    };

    const getTogglableColumns = (columns: GridColDef[]) => {
        return columns
          .filter((column) => column.field !== 'updatedDate')
          .map((column) => column.field);
      };

    const columns: GridColDef<GridValidRowModel>[] = [
        {
            field: "baseCode",
            headerName: "Base Code",
            sortable: true,
            width: 130,
        },
        {
            field: "marketingDesignerDisplayText",
            headerName: "Designer",
            sortable: true,
            width: 180,
        },
        {
            field: "seriesNameDisplayText",
            headerName: "Series Name",
            sortable: true,
            width: 180,
        },
        {
            field: "shortDesc",
            headerName: "Short Description",
            sortable: false,
            width: 250
        },
        {
            field: "userAssignments",
            headerName: "Assignments",
            sortable: false,
            width: 450,
            renderCell: (params) => {
                const userAssignments = Array.isArray(params.value)
                    ? params.value.map((userAssignment) =>
                        ({ id: userAssignment.id, name: `${userAssignment.assignment} - ${userAssignment.userName}` })
                    )
                    : [];

                return (
                    <GridCellChipsContent
                        onClose={sessionService.hasPermission(Permissions.ManageProductAssignments) ?
                            (value) => {
                                setSelectedAssignmentRow(params.row as ProductAssignments);
                                setSelectedDeleteRecord(value);
                                setShowAutoAssignmentConfirmModal(true);
                            }
                            : undefined}
                        classNames="product-assignments"
                        keyField="id"
                        displayTextFiled="name"
                        cellData={userAssignments}
                    />
                );
            }
        },
        {
            field: "stockCodeCount",
            headerName: "StockCodes Count",
            sortable: false,
            width: 130
        },
        { field: "updatedDate", headerName: "Updated Date", width: 0, disableColumnMenu: true },
    ];

    const gridToolbar = () => (
        <GridToolbarContainer>
            <div style={{ marginLeft: "12px" }}>
                <GridToolbarColumnsButton />
                <GridToolbarDensitySelector />
                <ExportProductAssignmentListing
                    productAssignmentTotalCount={userAssignmentsTotalCount}
                    setLoading={setLoading}
                />
            </div>
            <div style={{
                flexGrow: 1, textAlign: "center", padding: 5,
                display: "flex", justifyContent: "flex-end"
            }}>
                <GridToolbarQuickFilter sx={{ margin: "5px 30px 5px 5px", width: "30%" }} />
            </div>
        </GridToolbarContainer>
    );

    const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
        setRowModesModel(newRowModesModel);
    };

    const handleCloseAssignmentModal = () => {
        setAssignmentModal(false);
        setUserDropdownOptions([]);
    };

    const handleAutoAssignmentModalClose = () => {
        setShowAutoAssignmentConfirmModal(false);
    };

    const handleAutoAssignmentModalConfirm = async (autoAssignConfirm: string) => {
        if (autoAssignConfirm === "Yes") {
            const updatedDeleteRecords = selectedAssignmentRow.userAssignments.filter(us => us.id !== selectedDeleteRecord.id);
            await updateProductAssignments("delete", updatedDeleteRecords);
        }
        setShowAutoAssignmentConfirmModal(false);
    };

    const handleSaveAssignmentModal = async () => {
        await updateProductAssignments();
        handleCloseAssignmentModal();
    };

    const disableSaveButton = () => !!(selectedUserAssignments?.find(obj => obj?.assignmentId === ""));

    return (
        <div className="assignment-list">
            <Filters
                onApplyFilter={handleApplyFilterClick}
                filtersExpanded={filtersExpanded}
                handleExpandClick={handleExpandClick}
                viewMode={PermissionsFriendlyNames[VIEW_PRODUCT_ASSIGNMENT]}
            />
            <Loader isLoading={isLoading} />
            <Box
                sx={{
                    height: "70%",
                    width: "100%",
                    display: "block",
                }}
            >
                <DataGrid
                    rows={fetchedUserAssignments}
                    columns={sessionService.hasPermission(Permissions.ManageProductAssignments) ?
                        [actionColumn, ...columns] : [...columns]
                    }
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 10,
                            },
                        },
                        filter: {
                            filterModel: {
                                items: [],
                                quickFilterExcludeHiddenColumns: true
                            }
                        },
                        columns: {
                            columnVisibilityModel: {
                              updatedDate: false
                            },
                        },
                    }}
                    rowCount={userAssignmentsTotalCount}
                    pageSizeOptions={[10, 25, 50, 100]}
                    disableRowSelectionOnClick={true}
                    disableColumnFilter={true}
                    sortingMode="server"
                    paginationMode="server"
                    sortModel={[
                        {
                            field: getReverseSortFieldName(productAssignmentsListRequestState.sortBy),
                            sort:
                                productAssignmentsListRequestState.sortDirection == SortDirection.ASC
                                    ? "asc"
                                    : "desc",
                        },
                    ]}
                    onSortModelChange={(model, details) => {
                        onSortChange(model);
                    }}
                    onPaginationModelChange={onPaginationChange}
                    paginationModel={{
                        page: productAssignmentsListRequestState.pageIndex - 1,
                        pageSize: productAssignmentsListRequestState.pageSize,
                    }}
                    slots={{
                        toolbar: gridToolbar,
                        noRowsOverlay: () => !isLoading ? NO_ROWS_GRID_MESSAGE : undefined,
                        noResultsOverlay: () => !isLoading ? NO_LOCAL_SEARCH_RESULTS_GRID_MESSAGE : undefined,
                    }}
                    slotProps={{
                        columnsPanel: {
                          getTogglableColumns,
                        },
                      }}
                    getRowHeight={() => 'auto'}
                    rowModesModel={rowModesModel}
                    onRowModesModelChange={handleRowModesModelChange}
                />
            </Box>

            <Dialog className="assignment-modal" open={assignmentModal} sx={{ width: "100%" }} >
                <CloseIcon className="close-button" onClick={handleCloseAssignmentModal} />
                <UserAssignments
                    selectedUserAssignments={selectedUserAssignments}
                    setSelectedUserAssignments={setSelectedUserAssignments}
                    viewMode={PermissionsFriendlyNames[VIEW_PRODUCT_ASSIGNMENT]}
                    assignmentRoleDropdownOptions={assignmentRoleDropdownOptions}
                    userDropdownOptions={userDropdownOptions}
                    classNames="product-assignment-page"
                    baseCode={selectedAssignmentRow?.baseCode}
                    shortDesc={selectedAssignmentRow?.shortDesc}
                />
                <div className="modal-actions">
                    <Button className="save-btn" disabled={disableSaveButton()} onClick={handleSaveAssignmentModal}>Save</Button>
                    <Button className="cancel-btn" onClick={handleCloseAssignmentModal}>Cancel</Button>
                </div>
            </Dialog>

            <ConfirmationModal
                isOpen={showAutoAssignmentConfirmModal}
                onClose={handleAutoAssignmentModalClose}
                title={"Confirm Delete Assignment"}
                titleForConfirmYesButton={"Yes"}
                titleForConfirmNoButton={"No"}
                message={<>
                    Do you want to delete assignment <b>{selectedDeleteRecord?.name}</b> for base code <b>{selectedAssignmentRow?.baseCode}</b>?
                </>}
                handleConfirmYes={() => handleAutoAssignmentModalConfirm("Yes")}
                handleConfirmNo={() => handleAutoAssignmentModalConfirm("No")}
            />
        </div>
    );

};

export default AssignmentList;